import type { AuditEntry } from "@/types/api";

/**
 * An audit line, in the words of the person reading it.
 *
 * The raw actions are our vocabulary — `seat.claimed`, `seats.reclaimed` — and
 * a partner admin should never have to learn it to understand what happened in
 * their own organization. An audit trail nobody can read is a compliance
 * artefact, not a tool.
 */
export function describeAudit(e: AuditEntry): { text: string; who: string } {
	const d = e.detail ?? {};
	const target = e.target ?? "";
	const n = (k: string) => (d[k] == null ? null : String(d[k]));

	const text = ((): string => {
		switch (e.action) {
			case "seat.invited":
				return `Invited ${target}`;
			case "seat.claimed":
				return `${target} claimed their seat`;
			case "seat.deactivated":
				return `Removed ${target}`;
			case "member.role_changed":
				return `Changed ${target}'s role${n("role") ? ` to ${n("role")}` : ""}`;
			case "member.viewed":
				// Named-employee reporting is defensible only when it is itself
				// on the record, so this one is deliberately visible.
				return `Viewed ${target || "a member"}'s activity`;
			case "seats.purchased":
				return `Bought ${n("seats") ?? "additional"} seat${n("seats") === "1" ? "" : "s"}`;
			case "seats.granted":
				return `${n("seats") ?? "Seats"} seat${n("seats") === "1" ? "" : "s"} added`;
			case "seats.reclaimed":
				return `${n("seats") ?? "Seats"} seat${n("seats") === "1" ? "" : "s"} returned to the pool`;
			case "invoice.issued":
				return "Invoice issued";
			case "invoice.paid":
			case "invoice.paid_by_mandate":
				return `Invoice paid${e.action.endsWith("mandate") ? " automatically" : ""}`;
			case "org.settings_updated":
				return "Settings updated";
			case "org.suspended":
				return "Access paused for non-payment";
			case "content.added":
				return `Added “${n("title") ?? "a document"}”`;
			case "content.removed":
				return `Removed “${n("title") ?? "a document"}”`;
			case "mandate.activated":
				return "Automatic payment authorized";
			case "mandate.revoked_by_admin":
				return "Automatic payment removed";
			case "billing.collection_mode_changed":
				return n("mode") === "auto_charge"
					? "Turned on automatic payment"
					: "Switched to paying invoices manually";
			default:
				// Unknown actions still render, rather than vanishing. A trail
				// that silently drops what it does not recognise is worse than
				// one that shows a raw string.
				return e.action.replace(/[._]/g, " ");
		}
	})();

	// "System" is the honest actor for anything the platform did on a schedule —
	// attributing a sweep-driven suspension to a person would be a lie in the
	// one record that exists to be trusted.
	const who = e.actor_kind === "system" ? "Curo" : "An administrator";
	return { text, who };
}

/** Group by day, so a trail reads as a history rather than a flat list. */
export function groupByDay(entries: AuditEntry[]): [string, AuditEntry[]][] {
	const out = new Map<string, AuditEntry[]>();
	for (const e of entries) {
		const key = new Date(e.created_at).toLocaleDateString(undefined, {
			day: "numeric",
			month: "long",
			year: "numeric",
		});
		const list = out.get(key);
		if (list) list.push(e);
		else out.set(key, [e]);
	}
	return [...out.entries()];
}
